import * as React from "react";
import classNames from "classnames";
import {
	WithStyles,
	Theme,
	createStyles,
	withStyles,
	Chip,
	Button,
	TablePagination,
	ExpansionPanel,
	ExpansionPanelActions,
	ExpansionPanelSummary,
	ExpansionPanelDetails,
	Typography
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import DoneIcon from "@material-ui/icons/Done";
import ClearIcon from "@material-ui/icons/Clear";
import { connect } from "react-redux";
import { AuthInfo } from "../../api/token-storage";
import { Payment, getPayments } from '../../api/payments';
import { format, isAfter, differenceInHours } from 'date-fns';
import { StoreState } from '../../reducers/root-reducer';
import { Dispatch } from 'redux';
import { updatePayments, openAddPaymentDialog } from '../../actions/store/app-actions';
import { getPaymentsAndUpdateStore } from '../../actions/payment-actions';

interface PaymentTableProps extends WithStyles<typeof styles> {
	authInfo: AuthInfo;
}

type Props = PaymentTableProps &
	ReturnType<typeof mapStateToProps> &
	ReturnType<typeof mapDispatchToProps>;

export class PaymentList extends React.Component<Props> {
	public async componentDidMount() {
		getPaymentsAndUpdateStore(1);
	}

	private handlePageChange = (_: any, nextPage: number) => {
		getPaymentsAndUpdateStore(nextPage + 1);
	}

	private handleResendClick = (payment: Payment) => {
		this.props.openAddPaymentDialog(payment.inquiryId, payment.email, payment.name, payment.amount.toFixed(2), 0, payment.message, payment.id);
	}

	private handleCopyLinkClick = (payment: Payment) => {
		const el = document.createElement('input');
		el.value = 'https://res.pinzgauer-jaegerball.at/payments/' + payment.id;
		el.style.position = 'absolute';
		el.style.zIndex = '-9999';

		document.body.appendChild(el);

		el.select();
		document.execCommand('copy');
		document.body.removeChild(el);
	}

	private getStateName = (payment: Payment) => {
		switch (payment.state) {
			case "awaiting_payment":
				return "Erwarte Zahlungseingang";

			case "expired":
				return "Abgelaufen am " + format(payment.expirationDate, "DD.MM.YYYY HH:mm");

			case "pending":
				return "Zahlung wird im Moment verarbeitet";

			case "completed":
				return "Erfolgreich";
		}
	}

	private renderLog = (payment: Payment) => {
		const { classes } = this.props;

		if (!payment.log || payment.log.length === 0) return;

		return (
			<div className={classes.log}>
				{payment.log.reverse().map(log => (
					<React.Fragment key={log.id}>
						<div>
							<Typography className={classes.mono}>{format(log.createdAt, "MMM DD HH:mm:ss")}</Typography>
						</div>
						<div>
							<Typography className={classNames(classes.logType, classes.mono, {
								[classes.logTypeWarning]: log.type === "warning",
								[classes.logTypeError]: log.type === "error",
							})}>
								{log.type}
							</Typography>
						</div>
						<div>
							<Typography className={classes.mono}>{log.message}</Typography>
						</div>
					</React.Fragment>
				))}
			</div>
		);
	}

	public render() {
		const { classes, payments, page, count, authInfo } = this.props;

		return (
			<React.Fragment>
				{payments.map(p => (
					<ExpansionPanel
						key={p.requestId}
						className={classNames(classes.panel, {
							[classes.panelWaiting]: p.state === "awaiting_payment",
							[classes.panelExpired]: p.state === "expired",
							[classes.panelPending]: p.state === "pending",
							[classes.panelSuccess]: p.state === "completed",
						})}
					>
						<ExpansionPanelSummary
							expandIcon={<ExpandMoreIcon />}
						>
							<div className={classes.expansionSummary}>
								<Typography className={classes.summaryAmount}>{new Intl.NumberFormat('de-AT', { style: 'currency', currency: p.currency }).format(p.amount)}</Typography>
								<Typography style={{minWidth: 350}}>{p.email}</Typography>
								<Typography>{p.name}</Typography>
								<Typography className={classes.summaryDate}>{format(p.creationDate, "DD.MM.YYYY")}</Typography>
							</div>
						</ExpansionPanelSummary>
						<ExpansionPanelDetails className={classes.expansionDetails}>
							<div className={classes.attributes}>
								<Typography className={classes.attributeLabel}>Anfrage-ID</Typography>
								<Typography>{p.requestId}</Typography>
								<Typography className={classes.attributeLabel}>Status</Typography>
								<Typography>{this.getStateName(p)}</Typography>
								<Typography className={classes.attributeLabel}>Typ</Typography>
								<Typography>{p.inquiryId ? "Anfrage" : "Allgemeine Zahlung"}</Typography>
								{p.methodName && (
									<>
										<Typography className={classes.attributeLabel}>Zahlungsart</Typography>
										<Typography>{p.methodName}</Typography>
									</>
								)}
								{p.transactionId && (
									<>
										<Typography className={classes.attributeLabel}>Transaktions-ID</Typography>
										<Typography>{p.transactionId}</Typography>
									</>
								)}
								{p.state === "awaiting_payment" && (
									<>
										<Typography className={classes.attributeLabel}>Gültig bis</Typography>
										<Typography>{format(p.expirationDate, "DD.MM.YYYY HH:mm")}</Typography>
									</>
								)}
								{!p.completionTime.startsWith("0") && (
									<>
										<Typography className={classes.attributeLabel}>Abgeschlossen am</Typography>
										<Typography>{format(p.completionTime, "DD.MM.YYYY HH:mm")}</Typography>
									</>
								)}
								{authInfo.isAdmin && p.reservationId && (
									<>
										<Typography className={classes.attributeLabel}><small>Tareso Reservierung-ID</small></Typography>
										<Typography className={classes.selectableText}><small>{p.reservationId}</small></Typography>
									</>
								)}
								{authInfo.isAdmin && p.inquiryId && (
									<>
										<Typography className={classes.attributeLabel}><small>Tareso Anfrage-ID</small></Typography>
										<Typography className={classes.selectableText}><small>{p.inquiryId}</small></Typography>
									</>
								)}
							</div>
							{this.renderLog(p)}
						</ExpansionPanelDetails>
						<ExpansionPanelActions>
							{p.state === "awaiting_payment" && this.props.capabilities.platform !== "ios" && (
									<Button
										size="small"
										color="secondary"
										onClick={() => this.handleCopyLinkClick(p)}
									>
										Zahlungslink kopieren
									</Button>
							)}
							{p.state !== "completed" && (
									<Button
										size="small"
										color="secondary"
										onClick={() => this.handleResendClick(p)}
									>
										Erneut senden
									</Button>
							)}
						</ExpansionPanelActions>
					</ExpansionPanel>
				))}
				<TablePagination
					component="div"
					count={count}
					rowsPerPage={10}
					rowsPerPageOptions={[10]}
					page={page - 1}
					onChangePage={this.handlePageChange}
					labelDisplayedRows={(paginationInfo) => <span>Zeige {paginationInfo.from}-{paginationInfo.to} von {paginationInfo.count}</span>}
				/>
			</React.Fragment>
		);
	}
}

const styles = (theme: Theme) =>
	createStyles({
		attribute: {
			display: "flex"
		},
		attributes: {
			display: "grid",
			gridTemplateColumns: "auto 1fr",
			gridGap: "2px 20px",
			background: '#FAFAFA',
			padding: 24,
		},
		attributeLabel: {
			fontWeight: 500
		},
		selectableText: {
			userSelect: "text"
		},
		summaryAmount: {
			minWidth: 80,
			fontWeight: 500
		},
		summaryDate: {
			marginLeft: "auto"
		},
		panel: {
			borderLeft: "4px solid black"
		},
		panelWaiting: {
			borderColor: theme.status.payment.awaitingPayment
		},
		panelSuccess: {
			borderColor: theme.status.payment.completed
		},
		panelExpired: {
			borderColor: theme.status.payment.expired
		},
		panelPending: {
			borderColor: theme.status.payment.pending
		},
		panelFailed: {
			borderColor: "#db2828"
		},
		expansionSummary: {
			width: "100%",
			display: "flex",
			alignItems: "center",
		},
		expansionDetails: {
			padding: 0,
			display: 'block'
		},
		log: {
			padding: 24,
			background: '#F3F3F3',
			display: 'grid',
			gridTemplateColumns: 'max-content max-content 1fr',
			gridGap: '1px 25px',
		},
		logType: {
			textTransform: 'uppercase'
		},
		logTypeError: {
			color: '#e80c0c'
		},
		logTypeWarning: {
			color: '#ffa500'
		},
		mono: {
			fontFamily: '"Roboto Mono", monospace',
			fontSize: 14
		}
	});

const mapStateToProps = (state: StoreState) => ({
	capabilities: state.capabilities,
	payments: state.payments,
	authInfo: state.authInfo,
	page: state.paymentPage,
	count: state.paymentCount
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	openAddPaymentDialog: (inquiryId: string, email: string, name: string, amount: string, validHours: number, message: string, paymentId: string) => dispatch(openAddPaymentDialog(inquiryId, email, name, amount, validHours, message, paymentId))
})

export const ConnectedPaymentList = withStyles(styles)(
	connect(mapStateToProps, mapDispatchToProps)(PaymentList)
);
